"use client";

import type React from "react";
import { useLanguageContext } from "../../providers/language-provider";

export interface SkipLinkProps {
  children?: React.ReactNode;
  className?: string;
}

/**
 * Skip Link component for keyboard navigation accessibility.
 * Hidden by default, becomes visible when focused via keyboard (Tab).
 *
 * Auto-detects target: #filterbar (jobboard), #job (jobad), or #main-content (fallback)
 *
 * Reads `aria_skipToContent` from the language context by default.
 * Pass `children` to override the label.
 *
 * WCAG 2.4.1 - Bypass Blocks (Level A)
 */
export function SkipLink({ children, className }: SkipLinkProps) {
  const translations = useLanguageContext() as Record<string, string>;
  const label = children ?? translations.aria_skipToContent;
  const handleClick = () => {
    // Priority: #filterbar (jobboard) > #job (jobad) > #main-content (fallback)
    const target =
      document.querySelector<HTMLElement>("#filterbar") ||
      document.querySelector<HTMLElement>("#job") ||
      document.querySelector<HTMLElement>("#main-content");
    target?.focus({ preventScroll: true });
    target?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <button
      className={`sr-only focus:not-sr-only focus:fixed focus:z-[100] focus:rounded-md focus:bg-background focus:px-4 focus:py-2 focus:text-foreground focus:ring-2 focus:ring-ring focus:ring-offset-2 ${className || "focus:top-4 focus:left-4"}`}
      onClick={handleClick}
      type="button"
    >
      {label}
    </button>
  );
}
